import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, AlertTriangle, Edit2, User, Calendar, Hash, Building, CreditCard } from "lucide-react";
import { AiAvatar, resetGlobalSpeechState } from "../../components/ui/ai-avatar";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const IDLE_TIMEOUT = 180; // 3 minutes

export default function KioskBadgeReview() {
  const navigate = useNavigate();
  const location = useLocation();
  const { registrationData, ocrResult, badgeImages } = location.state || {};
  const idleTimerRef = useRef(null);
  
  // Unique submission ID for this attempt
  const submissionIdRef = useRef(`bb_submission_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    holder_name: ocrResult?.badge_data?.holder_name || "",
    badge_number: ocrResult?.badge_data?.badge_number || "",
    expiry_date: ocrResult?.badge_data?.expiry_date || "",
    issuing_authority: ocrResult?.badge_data?.issuing_authority || "",
    issue_date: ocrResult?.badge_data?.issue_date || "",
    badge_type: ocrResult?.badge_data?.badge_type || ""
  });

  const confidenceScore = ocrResult?.badge_data?.confidence_score || ocrResult?.confidence_score || 0;
  const isHighConfidence = confidenceScore >= 0.8;
  const requiresManualReview = ocrResult?.requires_manual_review || !ocrResult?.success;

  // Idle detection
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown', 'input'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  useEffect(() => {
    if (!registrationData) {
      navigate("/kiosk/category");
    }
    // Auto-enable editing for low confidence or failed OCR
    if (!isHighConfidence || requiresManualReview) {
      setIsEditing(true);
    }
  }, [registrationData, navigate, isHighConfidence, requiresManualReview]);

  if (!registrationData) {
    return null;
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      // Create registration with OCR data
      const payload = {
        vehicle_registration: registrationData.vehicle_registration,
        category: "disabled_blue_badge",
        email: registrationData.email,
        phone: registrationData.phone || "",
        full_name: formData.holder_name,
        site_id: registrationData.site_id || "site_001",
        kiosk_id: registrationData.kiosk_id || "kiosk_001",
        badge_data: {
          badge_number: formData.badge_number,
          holder_name: formData.holder_name,
          expiry_date: formData.expiry_date,
          issuing_authority: formData.issuing_authority,
          issue_date: formData.issue_date,
          badge_type: formData.badge_type,
          confidence_score: confidenceScore,
          front_image: badgeImages?.front,
          back_image: badgeImages?.back
        },
        requires_manual_review: requiresManualReview || !isHighConfidence
      };

      const response = await axios.post(`${API}/api/registrations/blue-badge`, payload);
      
      // Reset speech state before navigation
      resetGlobalSpeechState();
      
      navigate("/kiosk/confirmation", {
        state: {
          registration: response.data,
          requiresReview: requiresManualReview || !isHighConfidence,
          submissionId: submissionIdRef.current,
          outcomeType: (requiresManualReview || !isHighConfidence) ? "pending" : "success"
        }
      });
    } catch (error) {
      console.error("Registration error:", error);
      const errorDetail = error.response?.data?.detail || "An error occurred";
      
      // Check if it's a duplicate VRM error or other fatal error
      const isFatalError = errorDetail.includes("already registered") || 
                           error.response?.status === 400 ||
                           error.response?.status === 500;
      
      // Reset speech state before navigation
      resetGlobalSpeechState();
      
      if (isFatalError) {
        // Navigate to confirmation with explicit error state
        navigate("/kiosk/confirmation", {
          state: {
            registration: {
              ...registrationData,
              vehicle_registration: registrationData.vehicle_registration,
              category: "disabled_blue_badge",
              status: "error"
            },
            requiresReview: false,
            error: errorDetail,
            submissionId: submissionIdRef.current,
            outcomeType: "error"  // EXPLICIT error outcome
          }
        });
      } else {
        // True pending case - manual review needed
        navigate("/kiosk/confirmation", {
          state: {
            registration: {
              ...registrationData,
              full_name: formData.holder_name,
              status: "pending_review"
            },
            requiresReview: true,
            submissionId: submissionIdRef.current,
            outcomeType: "pending"  // EXPLICIT pending outcome
          }
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getAvatarMessage = () => {
    if (requiresManualReview) {
      return "We couldn't read all your badge details automatically. Please check and correct the information below, then submit for review.";
    }
    if (!isHighConfidence) {
      return "Please review your Blue Badge details below. Some fields may need correction. Make any changes needed, then confirm.";
    }
    return "Your Blue Badge details have been scanned successfully. Please review the information below and confirm it's correct.";
  };

  const formatConfidence = (score) => {
    if (score >= 0.9) return { label: "High", color: "text-green-600", bg: "bg-green-100" };
    if (score >= 0.7) return { label: "Medium", color: "text-amber-600", bg: "bg-amber-100" };
    return { label: "Low", color: "text-red-600", bg: "bg-red-100" };
  };

  const conf = formatConfidence(confidenceScore);

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 50%, #F0FDF4 100%)' }}
      data-testid="kiosk-badge-review"
    >
      {/* Header */}
      <header className="flex items-center justify-between p-6 md:p-8">
        <ParkWiseLogo size="lg" />
        
        {/* Progress */}
        <div className="flex items-center gap-2">
          <div className="progress-step completed"><CheckCircle className="w-5 h-5" /></div>
          <div className="progress-line completed w-8"></div>
          <div className="progress-step completed"><CheckCircle className="w-5 h-5" /></div>
          <div className="progress-line completed w-8"></div>
          <div className="progress-step completed"><CheckCircle className="w-5 h-5" /></div>
          <div className="progress-line completed w-8"></div>
          <div className="progress-step active">4</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col px-6 md:px-12 pb-8 overflow-y-auto">
        {/* AI Avatar */}
        <div className="flex justify-center mb-4">
          <AiAvatar 
            message={getAvatarMessage()} 
            size="small"
            autoSpeak={true}
          />
        </div>

        {/* Title */}
        <div className="text-center mb-4">
          <h1 
            className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Review Blue Badge Details
          </h1>
          {!requiresManualReview && (
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${conf.bg} ${conf.color}`}>
              <span>OCR Confidence: {conf.label} ({Math.round(confidenceScore * 100)}%)</span>
            </div>
          )}
        </div>

        {/* Manual Review Warning */}
        {requiresManualReview && (
          <div className="max-w-2xl mx-auto w-full mb-4">
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800">Manual Review Required</h3>
                <p className="text-amber-700 text-sm">
                  Your registration may require manual review before approval. Please enter your badge details below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Badge Images Preview */}
        {badgeImages && (badgeImages.frontPreview || badgeImages.backPreview) && (
          <div className="max-w-2xl mx-auto w-full mb-4">
            <div className="flex gap-4 justify-center">
              {badgeImages.frontPreview && (
                <div className="text-center">
                  <p className="text-xs text-slate-500 mb-1">Front</p>
                  <img 
                    src={badgeImages.frontPreview} 
                    alt="Badge Front" 
                    className="w-24 h-16 object-cover rounded-lg border-2 border-slate-200"
                  />
                </div>
              )}
              {badgeImages.backPreview && (
                <div className="text-center">
                  <p className="text-xs text-slate-500 mb-1">Back</p>
                  <img 
                    src={badgeImages.backPreview} 
                    alt="Badge Back" 
                    className="w-24 h-16 object-cover rounded-lg border-2 border-slate-200"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className="max-w-2xl mx-auto w-full">
          <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4">
            {/* Edit toggle for high confidence */}
            {isHighConfidence && !requiresManualReview && (
              <div className="flex justify-end">
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Edit2 className="w-4 h-4" />
                  {isEditing ? "Done Editing" : "Edit Details"}
                </button>
              </div>
            )}

            {/* Badge Holder Name */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-slate-700">
                <User className="w-4 h-4" />
                Badge Holder Name *
              </Label>
              {isEditing ? (
                <Input
                  value={formData.holder_name}
                  onChange={(e) => handleChange("holder_name", e.target.value)}
                  placeholder="Enter badge holder name"
                  className="h-12 text-lg"
                  data-testid="input-holder-name"
                />
              ) : (
                <div className="h-12 px-4 flex items-center bg-slate-50 rounded-lg text-lg text-slate-900">
                  {formData.holder_name || <span className="text-slate-400">Not detected</span>}
                </div>
              )}
            </div>

            {/* Badge Number */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-slate-700">
                <Hash className="w-4 h-4" />
                Blue Badge Serial Number
              </Label>
              {isEditing ? (
                <Input
                  value={formData.badge_number}
                  onChange={(e) => handleChange("badge_number", e.target.value)}
                  placeholder="Enter badge serial number"
                  className="h-12 text-lg font-mono"
                  data-testid="input-badge-number"
                />
              ) : (
                <div className="h-12 px-4 flex items-center bg-slate-50 rounded-lg text-lg font-mono text-slate-900">
                  {formData.badge_number || <span className="text-slate-400 font-sans">Not detected</span>}
                </div>
              )}
            </div>

            {/* Expiry Date */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-slate-700">
                <Calendar className="w-4 h-4" />
                Expiry Date
              </Label>
              {isEditing ? (
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => handleChange("expiry_date", e.target.value)}
                  className="h-12 text-lg"
                  data-testid="input-expiry-date"
                />
              ) : (
                <div className="h-12 px-4 flex items-center bg-slate-50 rounded-lg text-lg text-slate-900">
                  {formData.expiry_date ? new Date(formData.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : <span className="text-slate-400">Not detected</span>}
                </div>
              )}
            </div>

            {/* Issuing Authority */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-slate-700">
                <Building className="w-4 h-4" />
                Issuing Local Authority
              </Label>
              {isEditing ? (
                <Input
                  value={formData.issuing_authority}
                  onChange={(e) => handleChange("issuing_authority", e.target.value)}
                  placeholder="e.g. South Ayrshire Council"
                  className="h-12 text-lg"
                  data-testid="input-issuing-authority"
                />
              ) : (
                <div className="h-12 px-4 flex items-center bg-slate-50 rounded-lg text-lg text-slate-900">
                  {formData.issuing_authority || <span className="text-slate-400">Not detected</span>}
                </div>
              )}
            </div>

            {/* Issue Date (Optional) */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-slate-700">
                <CreditCard className="w-4 h-4" />
                Issue Date (if visible)
              </Label>
              {isEditing ? (
                <Input
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => handleChange("issue_date", e.target.value)}
                  className="h-12 text-lg"
                  data-testid="input-issue-date"
                />
              ) : (
                <div className="h-12 px-4 flex items-center bg-slate-50 rounded-lg text-lg text-slate-900">
                  {formData.issue_date ? new Date(formData.issue_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : <span className="text-slate-400">Not detected</span>}
                </div>
              )}
            </div>

            {/* Vehicle Registration - Read Only */}
            <div className="pt-4 border-t border-slate-200 space-y-2">
              <Label className="text-slate-500 text-sm">Vehicle Registration</Label>
              <div className="h-12 px-4 flex items-center bg-slate-100 rounded-lg text-lg font-mono text-slate-700">
                {registrationData.vehicle_registration}
              </div>
            </div>

            {/* Email - Read Only */}
            <div className="space-y-2">
              <Label className="text-slate-500 text-sm">Email Address</Label>
              <div className="h-12 px-4 flex items-center bg-slate-100 rounded-lg text-lg text-slate-700">
                {registrationData.email}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Navigation */}
      <footer className="px-6 py-6 border-t-2 border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/kiosk/badge-scan", { state: { registrationData } })}
            disabled={submitting}
            className="kiosk-btn-secondary flex items-center gap-3"
            data-testid="back-button"
          >
            <ArrowLeft className="w-6 h-6" />
            <span>Rescan Badge</span>
          </button>

          <button
            onClick={handleSubmit}
            disabled={submitting || !formData.holder_name}
            className="kiosk-btn-primary flex items-center gap-3"
            data-testid="confirm-button"
          >
            <span>{submitting ? "Submitting..." : (requiresManualReview ? "Submit for Review" : "Confirm & Register")}</span>
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>
      </footer>
    </div>
  );
}
